import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { canAccess } from '../../constants/tiers';
import CreateGroupModal from '../../components/CreateGroupModal';
import { toast } from 'sonner';
import {
  Search, MapPin, Plus, Loader2, ChevronLeft, Lock, Users, UsersRound,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Peer Circles → Groups list (iter 76 Phase 3).
 *
 * Gated to Community + Connected. Search by name (q) + filter by city.
 * "Create group" opens CreateGroupModal; on success we navigate straight
 * to the new group's detail page.
 *
 * Cards show: photo (or cream avatar tile), name, city, member count,
 * primary CTA (Open / Join). Once a member, primary CTA → Open. Otherwise
 * single-shot Join button that flips the card to is_member=true and routes
 * to detail page.
 */
const GroupCard = ({ g, busy, onJoin }) => {
  return (
    <div
      className="bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden hover:border-[#2CA8A0]/55 transition flex flex-col"
      data-testid={`group-card-${g.group_id}`}
    >
      {g.photo_url ? (
        <Link to={`/portal/peer-circles/groups/${g.group_id}`} className="block">
          <img src={g.photo_url} alt="" className="w-full h-40 object-cover" loading="lazy" />
        </Link>
      ) : (
        <Link
          to={`/portal/peer-circles/groups/${g.group_id}`}
          className="w-full h-40 bg-[#F4ECD8] flex items-center justify-center"
        >
          <Users className="w-10 h-10 text-[#1A5C6E]/45" />
        </Link>
      )}

      <div className="p-5 flex-1 flex flex-col">
        <Link
          to={`/portal/peer-circles/groups/${g.group_id}`}
          className="font-display italic text-xl text-[#0A1F2E] hover:text-[#1A5C6E] leading-snug block"
          data-testid={`group-card-name-${g.group_id}`}
        >
          {g.name}
        </Link>
        <div className="flex items-center gap-3 text-xs text-[#0A1F2E]/60 mt-1.5">
          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {g.city}</span>
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {g.member_count}</span>
        </div>
        <p className="text-sm text-[#0A1F2E]/70 mt-3 line-clamp-3 flex-1">{g.description}</p>

        <div className="mt-4">
          {g.is_member ? (
            <Link
              to={`/portal/peer-circles/groups/${g.group_id}`}
              className="inline-flex items-center justify-center font-mono uppercase text-[10px] tracking-[0.15em] bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/55 text-[#0A1F2E] px-4 py-2 rounded-full transition"
              data-testid={`group-card-open-${g.group_id}`}
            >
              Open
            </Link>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => onJoin(g)}
              className="inline-flex items-center justify-center font-mono uppercase text-[10px] tracking-[0.15em] bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white px-4 py-2 rounded-full transition"
              data-testid={`group-card-join-${g.group_id}`}
            >
              {busy ? 'Joining…' : 'Join group'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PortalPeerCirclesGroups = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const unlocked = canAccess(user?.role, 'peerCircles');

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchGroups = useCallback(async () => {
    if (!unlocked) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (city.trim()) params.set('city', city.trim());
      const url = `${API_URL}/api/peer-circles/groups${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail?.message || d.detail || 'Could not load groups');
      }
      const data = await res.json();
      setGroups(data.groups || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [q, city, unlocked]);

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchGroups();
  };

  const onJoin = async (g) => {
    setBusyId(g.group_id);
    try {
      const res = await fetch(`${API_URL}/api/peer-circles/groups/${g.group_id}/join`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail?.message || d.detail || 'Could not join');
      }
      toast.success(`Joined ${g.name}.`);
      navigate(`/portal/peer-circles/groups/${g.group_id}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (!unlocked) {
    return (
      <PortalLayout activeTab="peerCircles">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-14" data-testid="groups-locked">
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F4ECD8] flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-[#1A5C6E]" />
            </div>
            <h1 className="font-display italic text-3xl text-[#0A1F2E] mb-2">Groups is part of Peer Circles</h1>
            <p className="text-[#0A1F2E]/70 mb-6">Unlock groups, DMs, and the directory with a Community or Connected plan.</p>
            <Link
              to="/portal/upgrade"
              className="inline-flex items-center gap-1 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition"
              data-testid="groups-upgrade-cta"
            >
              See plans
            </Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="peerCircles">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="groups-page">
        <button
          type="button"
          onClick={() => navigate('/portal/peer-circles')}
          className="inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E] text-sm mb-6 transition font-mono uppercase tracking-[0.12em] text-[11px]"
          data-testid="groups-back-to-hub"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Peer Circles
        </button>

        <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
              Peer Circles · Groups
            </p>
            <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
              City crews & interest groups.
            </h1>
            <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
              Join an existing group or start your own. Each group has its own message board.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-2.5 rounded-full transition self-start md:self-auto"
            data-testid="groups-create-btn"
          >
            <Plus className="w-4 h-4" /> Create group
          </button>
        </header>

        <form
          onSubmit={onSearch}
          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 mb-8"
          data-testid="groups-search-form"
        >
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1F2E]/40" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name…"
              className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-full pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/20"
              data-testid="groups-search-q"
            />
          </div>
          <div className="relative">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1F2E]/40" />
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Filter by city…"
              className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-full pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/20"
              data-testid="groups-search-city"
            />
          </div>
          <button
            type="submit"
            className="bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-2.5 rounded-full transition"
            data-testid="groups-search-submit"
          >
            Search
          </button>
        </form>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 mb-6 rounded-xl text-sm" data-testid="groups-error">
            {error}
          </div>
        )}

        {!loading && !error && groups.length === 0 && (
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-12 text-center" data-testid="groups-empty">
            <UsersRound className="w-12 h-12 text-[#1A5C6E]/40 mx-auto mb-3" />
            <p className="text-[#0A1F2E]/65 mb-5">No groups yet - be the first to start one.</p>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-2.5 rounded-full transition"
              data-testid="groups-empty-create-btn"
            >
              <Plus className="w-4 h-4" /> Create group
            </button>
          </div>
        )}

        {!loading && !error && groups.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="groups-grid">
            {groups.map((g) => (
              <GroupCard
                key={g.group_id}
                g={g}
                busy={busyId === g.group_id}
                onJoin={onJoin}
              />
            ))}
          </div>
        )}

        <CreateGroupModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={(g) => {
            setGroups((cur) => [{ ...g, is_member: true }, ...cur]);
            navigate(`/portal/peer-circles/groups/${g.group_id}`);
          }}
        />
      </div>
    </PortalLayout>
  );
};

export default PortalPeerCirclesGroups;
